import { getApp, getAppBanner } from "../apps/catalog.js";
import {
  isFavorite,
  listFavorites,
  toggleFavorite,
} from "../apps/favorites.js";
import { getItem } from "../safe-storage.js";
import { getHomeIdentity, requestOidcLaunchUrl } from "./launch-oidc.js";
import { navigate } from "./router.js";
import { bindAppInvite } from "./app-invite.js";
import { resolveInvitedIframeSrc } from "./embed-url.js";

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function readStored(key) {
  return getItem(key) || "";
}

/** Append home-screen identity params to same-origin migrated apps. */
function resolveAppUrl(app) {
  const url = app.url || "";
  const isLocalApp = url.startsWith("/apps/");
  if (!isLocalApp) return url;

  const params = new URLSearchParams(window.location.search);
  const userId = params.get("userId") || readStored("w2l_uid");
  const nickname = params.get("nickname") || readStored("w2l_nickname");
  const spaceId = params.get("spaceId") || readStored("w2l_space_id");
  const mapId = params.get("mapId") || readStored("w2l_map_id");

  const target = new URL(url, window.location.origin);
  if (userId && !target.searchParams.get("userId")) {
    target.searchParams.set("userId", userId);
  }
  if (nickname && !target.searchParams.get("nickname")) {
    target.searchParams.set("nickname", nickname);
  }
  if (spaceId && !target.searchParams.get("spaceId")) {
    target.searchParams.set("spaceId", spaceId);
  }
  if (mapId && !target.searchParams.get("mapId")) {
    target.searchParams.set("mapId", mapId);
  }
  return `${target.pathname}${target.search}`;
}

function launchErrorMessage(error) {
  const code = error && error.message ? String(error.message) : "";
  if (code === "missing_identity") {
    return "افتح الصفحة من حسابك أولاً (مطلوب userId و spaceId).";
  }
  return "تعذر تسجيل الدخول إلى التطبيق. حاول مرة أخرى.";
}

function renderExternalStage(app, appUrl) {
  return `
    <div class="app-external">
      <div class="app-external-icon" aria-hidden="true">${
        app.icon && !String(app.icon).startsWith("/") && !String(app.icon).startsWith("http")
          ? escapeHtml(app.icon)
          : "🔗"
      }</div>
      <h2 class="app-external-title">${escapeHtml(app.name)}</h2>
      <p class="app-external-copy">هذا التطبيق يفتح في تبويب جديد</p>
      <a class="primary-btn" href="${escapeHtml(appUrl)}" target="_blank" rel="noopener noreferrer">فتح ${escapeHtml(app.name)}</a>
    </div>
  `;
}

function renderLoadingStage(app) {
  return `
    <div class="app-external">
      <div class="app-external-icon" aria-hidden="true">${
        app.icon && !String(app.icon).startsWith("/") && !String(app.icon).startsWith("http")
          ? escapeHtml(app.icon)
          : "⏳"
      }</div>
      <h2 class="app-external-title">${escapeHtml(app.name)}</h2>
      <p class="app-external-copy">جاري تسجيل الدخول…</p>
    </div>
  `;
}

function renderErrorStage(app, message) {
  return `
    <div class="app-external">
      <div class="app-external-icon" aria-hidden="true">${
        app.icon && !String(app.icon).startsWith("/") && !String(app.icon).startsWith("http")
          ? escapeHtml(app.icon)
          : "⚠️"
      }</div>
      <h2 class="app-external-title">${escapeHtml(app.name)}</h2>
      <p class="app-external-copy">${escapeHtml(message)}</p>
      <button type="button" class="primary-btn" data-action="retry-launch">إعادة المحاولة</button>
    </div>
  `;
}

function iframeAllow(app) {
  const permissions = [
    "fullscreen",
    "clipboard-read",
    "clipboard-write",
  ];
  if (app && app.id === "watchparty") {
    permissions.push("autoplay", "encrypted-media", "display-capture");
  }
  return permissions.join("; ");
}

function setStage(root, app, html, framed) {
  const stage = root.querySelector(".app-stage");
  if (!stage) return;
  stage.classList.toggle("has-frame", !!framed);
  stage.innerHTML = html;
}

function updateFavoriteButton(favoriteBtn, favored) {
  if (!favoriteBtn) return;
  favoriteBtn.classList.toggle("is-favorite", favored);
  favoriteBtn.setAttribute("aria-pressed", String(favored));
  favoriteBtn.setAttribute("aria-label", favored ? "إزالة من المفضلة" : "إضافة للمفضلة");
  favoriteBtn.textContent = favored ? "★" : "☆";
}

async function mountOidcApp(root, app) {
  setStage(root, app, renderLoadingStage(app), false);

  try {
    const launchUrl = await requestOidcLaunchUrl({ app: app.id });
    if (app.embeddable) {
      setStage(
        root,
        app,
        `<iframe class="app-frame" title="${escapeHtml(app.name)}" src="${escapeHtml(launchUrl)}" allow="${iframeAllow(app)}"></iframe>`,
        true,
      );
      return;
    }
    setStage(root, app, renderExternalStage(app, launchUrl), false);
  } catch (error) {
    setStage(root, app, renderErrorStage(app, launchErrorMessage(error)), false);
    const retry = root.querySelector('[data-action="retry-launch"]');
    if (retry) {
      retry.addEventListener("click", () => {
        mountOidcApp(root, app);
      });
    }
  }
}

export function renderAppPage(root, appId) {
  const app = getApp(appId);

  if (!app) {
    root.innerHTML = `
      <div class="app-shell" dir="rtl">
        <header class="app-topbar">
          <div class="app-topbar-actions">
            <button type="button" class="icon-btn" data-action="back" aria-label="العودة للرئيسية">→</button>
          </div>
          <div class="app-topbar-heading">
            <h1 class="app-topbar-title">التطبيق غير موجود</h1>
          </div>
        </header>
        <main class="app-stage">
          <p class="app-missing">هذا التطبيق غير موجود في القائمة بعد.</p>
          <button type="button" class="primary-btn" data-action="back">العودة للرئيسية</button>
        </main>
      </div>
    `;
    bindChrome(root, null, false);
    return;
  }

  const identity = getHomeIdentity();
  const canFavorite = Boolean(identity.userId && identity.spaceId);
  const favored = canFavorite && isFavorite(app.id);
  const needsOidc = !!app.oidc;
  const description = app.description || "";
  const banner = getAppBanner(app);
  const bannerPill = banner
    ? `<span class="app-banner-pill is-${banner.color}">${escapeHtml(banner.label)}</span>`
    : "";

  let stage;
  if (needsOidc) {
    stage = renderLoadingStage(app);
  } else {
    const invitedSrc = resolveInvitedIframeSrc(app);
    const appUrl = invitedSrc || resolveAppUrl(app);
    stage = app.embeddable
      ? `<iframe class="app-frame" title="${escapeHtml(app.name)}" src="${escapeHtml(appUrl)}" allow="${iframeAllow(app)}"></iframe>`
      : renderExternalStage(app, appUrl);
  }

  root.innerHTML = `
    <div class="app-shell" dir="rtl">
      <header class="app-topbar">
        <div class="app-topbar-actions">
          <button type="button" class="icon-btn" data-action="back" aria-label="العودة للرئيسية">→</button>
          <button type="button" class="icon-btn favorite-btn ${favored ? "is-favorite" : ""}" data-action="favorite" aria-label="${favored ? "إزالة من المفضلة" : "إضافة للمفضلة"}" aria-pressed="${favored}" ${canFavorite ? "" : "disabled"}>
            ${favored ? "★" : "☆"}
          </button>
        </div>
        <div class="app-topbar-heading">
          <div class="app-topbar-title-row">
            <h1 class="app-topbar-title">${escapeHtml(app.name)}</h1>
            ${bannerPill}
          </div>
          ${
            description
              ? `<p class="app-topbar-description">${escapeHtml(description)}</p>`
              : ""
          }
        </div>
        ${
          canFavorite
            ? `
        <div class="app-topbar-end" data-invite-wrap>
          <button type="button" class="icon-btn favorited-by-toggle" data-action="invite-toggle" aria-label="دعوة" aria-expanded="false" aria-haspopup="true" title="دعوة">
            👥
          </button>
          <div class="favorited-by-panel invite-panel" data-invite-panel hidden>
            <input class="invite-search" data-invite-search type="text" inputmode="search" placeholder="ابحث بالاسم الأول" autocomplete="off" />
            <div class="favorited-by-list" data-invite-results></div>
            <div class="invite-selected" data-invite-selected></div>
            <button type="button" class="favorited-by-invite-all" data-action="invite-send" disabled>دعوة</button>
            <p class="favorited-by-status" data-invite-status hidden></p>
          </div>
        </div>
        `
            : ""
        }
      </header>
      <main class="app-stage ${!needsOidc && app.embeddable ? "has-frame" : ""}">
        ${stage}
      </main>
    </div>
  `;

  bindChrome(root, app, canFavorite);
  if (canFavorite) {
    bindAppInvite(root, app);
    listFavorites()
      .then(() => {
        updateFavoriteButton(root.querySelector('[data-action="favorite"]'), isFavorite(app.id));
      })
      .catch(() => {});
  }

  if (needsOidc) {
    if (!identity.userId || !identity.spaceId) {
      setStage(root, app, renderErrorStage(app, launchErrorMessage(new Error("missing_identity"))), false);
      return;
    }
    mountOidcApp(root, app);
  }
}

function bindChrome(root, app, canFavorite) {
  root.querySelectorAll('[data-action="back"]').forEach((el) => {
    el.addEventListener("click", () => {
      const search = window.location.search || "";
      navigate(`/${search}`, { replace: false });
    });
  });

  const favoriteBtn = root.querySelector('[data-action="favorite"]');
  if (favoriteBtn && app && canFavorite) {
    favoriteBtn.addEventListener("click", async () => {
      if (favoriteBtn.disabled) return;
      favoriteBtn.disabled = true;
      try {
        const result = await toggleFavorite(app.id);
        updateFavoriteButton(favoriteBtn, result.isFavorite);
      } catch {
        // Keep previous UI state on failure.
      } finally {
        favoriteBtn.disabled = false;
      }
    });
  }
}
